// Pure value logic for DateBox: parsing the canonical string into editable parts, building it back,
// and formatting the display text. Framework-free — no React, no side effects.

import { pad2, parseIsoDate } from './dateCore';
import type { DateLocaleData } from './dateLocale';
import { extractTimes, parseOneDate, parseRange, splitDateTime } from './dateParse';

export interface ValueParts {
  from: string | null;
  to: string | null;
  fH: number | null;
  fM: number | null;
  tH: number | null;
  tM: number | null;
}

export const EMPTY_PARTS: ValueParts = {
  from: null,
  to: null,
  fH: null,
  fM: null,
  tH: null,
  tM: null,
};

/** A real (pickable) endpoint — not empty and not an ∞ sentinel. */
export function isReal(value: string | null): value is string {
  return !!value && value !== 'INF' && value !== '-INF';
}

export function todayDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Anchor calendar month for a set of parts (first real endpoint, else today). */
export function anchorMonth(parts: ValueParts): { viewYear: number; viewMonth: number } {
  const anchor = isReal(parts.from) ? parts.from : isReal(parts.to) ? parts.to : null;
  const base = anchor ? (parseIsoDate(anchor) ?? todayDate()) : todayDate();
  return { viewYear: base.getFullYear(), viewMonth: base.getMonth() };
}

/** Parse the `timeStep` combo (`"5 / 4"`) into a step + column count. */
export function parseTimeStep(combo: string | undefined): { step: number; cols: number } {
  const [rawStep, rawCols] = String(combo || '5 / 4').split('/');
  return {
    step: Math.max(1, parseInt(rawStep ?? '5', 10) || 5),
    cols: Math.max(1, parseInt(rawCols ?? '4', 10) || 4),
  };
}

/** Parse a canonical value string into its editable parts + the calendar anchor month. */
export function parseValue(
  value: string,
  mode: 'single' | 'range',
  withTime: boolean
): ValueParts & { viewYear: number; viewMonth: number } {
  const v = (value || '').trim();
  let rawFrom: string | null = null;
  let rawTo: string | null = null;
  if (mode === 'range') {
    const parts = v.split('..');
    rawFrom = parts[0] || null;
    rawTo = parts[1] || null;
  } else {
    rawFrom = v || null;
  }
  const fp = splitDateTime(rawFrom);
  const tp = splitDateTime(rawTo);
  const from = fp.date;
  const to = tp.date;
  let fH = fp.h;
  let fM = fp.m;
  let tH = tp.h;
  let tM = tp.m;
  if (withTime) {
    if (isReal(from) && fH == null) {
      fH = 0;
      fM = 0;
    }
    if (isReal(to) && tH == null) {
      tH = 0;
      tM = 0;
    }
  }
  return { from, to, fH, fM, tH, tM, ...anchorMonth({ from, to, fH, fM, tH, tM }) };
}

function timeSuffix(h: number | null, m: number | null, withTime: boolean): string {
  return withTime && h != null ? `T${pad2(h)}:${pad2(m ?? 0)}` : '';
}

function endpoint(
  value: string | null,
  h: number | null,
  m: number | null,
  withTime: boolean
): string {
  if (!value) return '';
  if (value === 'INF' || value === '-INF') return value;
  return value + timeSuffix(h, m, withTime);
}

/** Build the canonical value string from parts. */
export function buildValue(parts: ValueParts, mode: 'single' | 'range', withTime: boolean): string {
  if (mode === 'range') {
    if (!parts.from && !parts.to) return '';
    return `${endpoint(parts.from, parts.fH, parts.fM, withTime)}..${endpoint(parts.to, parts.tH, parts.tM, withTime)}`;
  }
  return parts.from ? endpoint(parts.from, parts.fH, parts.fM, withTime) : '';
}

/** A value is complete (and thus emittable): single needs `from`; range needs both ends. */
export function isComplete(parts: ValueParts, mode: 'single' | 'range'): boolean {
  return mode === 'single' ? !!parts.from : !!parts.from && !!parts.to;
}

/** Format the parts as the field's display text for the active locale. */
export function displayText(
  parts: ValueParts,
  mode: 'single' | 'range',
  withTime: boolean,
  locale: DateLocaleData
): string {
  const fmt = (iso: string | null) => (iso ? locale.format(parseIsoDate(iso)!) : '');
  const tt = (h: number | null, m: number | null) =>
    withTime && h != null ? ` ${pad2(h)}:${pad2(m ?? 0)}` : '';
  const { from, to, fH, fM, tH, tM } = parts;
  if (mode === 'range') {
    const fromReal = isReal(from);
    const toReal = isReal(to);
    if (fromReal && to === 'INF') return `${locale.rangeFrom} ${fmt(from)}${tt(fH, fM)}`;
    if (from === '-INF' && toReal) return `${locale.rangeTo} ${fmt(to)}${tt(tH, tM)}`;
    if (fromReal && toReal) return `${fmt(from)}${tt(fH, fM)} – ${fmt(to)}${tt(tH, tM)}`;
    if (fromReal) return `${fmt(from)}${tt(fH, fM)} – …`;
    return '';
  }
  return from ? `${fmt(from)}${tt(fH, fM)}` : '';
}

export interface CommitOptions {
  mode: 'single' | 'range';
  withTime: boolean;
  locale: DateLocaleData;
  openStart: boolean;
  openEnd: boolean;
  /** Meze pro pivot dvojciferného roku při psaní (ISO). */
  min: string | null;
  max: string | null;
  isDisabled: (iso: string) => boolean;
  current: ValueParts;
}

/** Outcome of parsing the typed text on commit — the component maps this onto state. */
export type CommitResult =
  | { kind: 'clear' }
  | { kind: 'noop' }
  | { kind: 'invalid' }
  | { kind: 'set'; parts: ValueParts };

/** Pure commit logic: parse the typed text into a value (or clear/noop/invalid). No side effects. */
export function parseCommit(raw: string, opts: CommitOptions): CommitResult {
  const { mode, withTime, locale, openStart, openEnd, min, max, isDisabled, current } = opts;
  const bounds = { min, max };
  if (raw === '') return { kind: 'clear' };

  let datePart = raw;
  let times: Array<{ h: number; m: number }> = [];
  if (withTime) {
    const ex = extractTimes(raw);
    datePart = ex.clean;
    times = ex.times;
  }

  if (mode === 'range') {
    if (raw === displayText(current, mode, withTime, locale)) return { kind: 'noop' };
    const parsed = parseRange(datePart, locale, { openStart, openEnd, bounds });
    if (!parsed) return { kind: 'invalid' };
    const realF = isReal(parsed.from);
    const realT = isReal(parsed.to);
    if ((realF && isDisabled(parsed.from!)) || (realT && isDisabled(parsed.to!))) {
      return { kind: 'invalid' };
    }
    const parts: ValueParts = {
      from: parsed.from,
      to: parsed.to,
      fH: current.fH,
      fM: current.fM,
      tH: current.tH,
      tM: current.tM,
    };
    if (withTime) {
      if (times[0]) {
        parts.fH = times[0].h;
        parts.fM = times[0].m;
      } else if (realF && parts.fH == null) {
        parts.fH = 0;
        parts.fM = 0;
      }
      if (times[1]) {
        parts.tH = times[1].h;
        parts.tM = times[1].m;
      } else if (realT && parts.tH == null) {
        parts.tH = 0;
        parts.tM = 0;
      }
    }
    return { kind: 'set', parts };
  }

  const a = parseOneDate(datePart, locale, bounds);
  if (!a || isDisabled(a)) return { kind: 'invalid' };
  const parts: ValueParts = { ...current, from: a, to: null };
  if (withTime) {
    if (times[0]) {
      parts.fH = times[0].h;
      parts.fM = times[0].m;
    } else if (parts.fH == null) {
      parts.fH = 0;
      parts.fM = 0;
    }
  }
  return { kind: 'set', parts };
}
